import { formatDate, IconCheckmarkRoundedFilled, IconErrorFilled } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { InvoiceStatus } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import type { QuoteStatus } from 'receivable-invoices-js/react/api/models/quote';
import styles from './status-and-date.module.css';

interface StatusAndDateProps {
  isQuote: boolean;
  status: InvoiceStatus | QuoteStatus;
  date?: string | null;
}

export function StatusAndDate({ isQuote, status, date }: StatusAndDateProps): ReactNode {
  const { locale, formatMessage } = useIntl();

  const hasDate = Boolean(date && date !== '');
  const dateValue: string | null = hasDate && date ? date : null;

  if (isQuote) {
    switch (status) {
      case 'pending_approval': {
        if (!hasDate || !dateValue) {
          return null;
        }
        const formattedDatePending = formatDate({ date: dateValue, locale, token: 'date-year-s' });
        const hasExpired = new Date(dateValue) < new Date();
        return (
          <p className={`${styles.formattedDate} body-1`} data-test-status-date>
            {hasExpired
              ? formatMessage({ id: 'client-portal.expired-on' }, { date: formattedDatePending })
              : formatMessage(
                  { id: 'quote.preview.expiration-label' },
                  { date: formattedDatePending }
                )}
          </p>
        );
      }
      case 'approved': {
        if (!hasDate || !dateValue) {
          return (
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="approved"
            />
          );
        }
        const formattedDateApproved = formatDate({ date: dateValue, locale, token: 'date-year-s' });
        return (
          <>
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="approved"
            />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.approved-on' }, { date: formattedDateApproved })}
            </p>
          </>
        );
      }
      case 'canceled': {
        if (!hasDate || !dateValue) {
          return <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />;
        }
        const formattedDateCanceled = formatDate({ date: dateValue, locale, token: 'date-year-s' });
        return (
          <>
            <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage(
                { id: 'client-portal.canceled-on.quote' },
                { date: formattedDateCanceled }
              )}
            </p>
          </>
        );
      }
      default:
        return null;
    }
  } else {
    switch (status) {
      case 'unpaid': {
        if (!hasDate || !dateValue) {
          return null;
        }
        // Validate that the date is a valid date string before formatting
        const dateObj = new Date(dateValue);
        if (isNaN(dateObj.getTime())) {
          return null;
        }
        const formattedDateUnpaid = formatDate({ date: dateValue, locale, token: 'date-year-s' });
        return (
          <p className={`${styles.formattedDate} body-1`} data-test-status-date>
            {formatMessage({ id: 'client-portal.due-date' }, { date: formattedDateUnpaid })}
          </p>
        );
      }
      case 'paid': {
        if (!hasDate || !dateValue) {
          return (
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="paid"
            />
          );
        }
        const formattedDatePaid = formatDate({ date: dateValue, locale, token: 'date-year-s' });
        return (
          <>
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="paid"
            />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.paid-on.invoice' }, { date: formattedDatePaid })}
            </p>
          </>
        );
      }
      case 'canceled': {
        if (!hasDate || !dateValue) {
          return <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />;
        }
        const formattedDateCanceledInvoice = formatDate({
          date: dateValue,
          locale,
          token: 'date-year-s',
        });
        return (
          <>
            <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage(
                { id: 'client-portal.canceled-on.invoice' },
                { date: formattedDateCanceledInvoice }
              )}
            </p>
          </>
        );
      }
      default:
        return null;
    }
  }
}
